import CommentLayout from "app/uicomponents/macrocomponents/Comments/CommentLayout";
import BrandingIconButton from "app/uicomponents/macrocomponents/InteractiveProposal/BrandingIconButton";
import HexagonCheckIcon from "app/assets/IconComponent/HexagonCheckIcon";
import { getSessionStore } from "app/utils/sessionStore";
import { capitalizeNames, getOpenOrUnresolvedComments, getThemeColors } from "app/utils/utils";
import { FaRegCircleCheck } from "react-icons/fa6";
import { Divider } from "primereact/divider";
import React, { MutableRefObject, useRef } from "react";
import { makeRequest } from "app/utils/makeRequest";
import { CurrentProposalType, PROPOSAL_STATUSES, TOASTTYPE, USERTYPE } from "app/utils/types";
import { getObjectFromLocalStore } from "app/utils/localStore";
import { Toast } from "primereact/toast";
import ScreenLoader from "app/uicomponents/microcomponents/ScreenLoader";
import UploadSection from "./UploadSection";
import { useComments } from "app/uicomponents/macrocomponents/Comments/CommentContext";
import { useSelector } from "react-redux";
import { getIsAgencyManagerActiveData } from "app/redux/features/isAgencyActive";
import { useTranslation } from "react-i18next";
import CommonHookForAPI from "app/HOC/CommonHookForAPI";
import { useIPContext } from "../../InteractiveProposalProvider";

// Approved signers render as a green chip (name + check) with the approval timestamp beneath,
// matching the Review Center signature design.
const formatApprovedAt = (iso: string): string => {
  const d = new Date(iso);
  return `${d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })} @ ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}`;
};

const SignedPill = ({ name }: { name: string }) => (
  <div className="flex items-center gap-1 px-2 py-1 rounded-full border border-[#188A42] border-[1px] text-[#188A42]">
    <span className="text-[13px] font-semibold whitespace-nowrap">{name}</span>
    <HexagonCheckIcon fill="#188A42" width={16} height={16} />
  </div>
);

// Pending (not-yet-approved) signer: a plain outlined pill with the signer's name.
const PendingPill = ({ name }: { name: string }) => (
  <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-[#E0E0E0] border-[1px]">
    <span className="text-[16px] font-semibold whitespace-nowrap text-[#004781]">{name}</span>
  </div>
);

const ApprovedTimestamp = ({ value }: { value: string }) => (
  <p className="text-[11px] text-[#99A1AF] mt-[9px] text-center">{value}</p>
);

// 3-column signer layout (Primary Contact / Advisor / Insured(s)) — mirrors the Review Center.
const SignerColumn = ({
  title,
  children,
  hasBorderRight = false,
}: {
  title: string;
  children: React.ReactNode;
  hasBorderRight?: boolean;
}) => (
  <div
    className="flex flex-col items-center px-6"
    style={hasBorderRight ? { borderRight: "1px solid #E5E7EB" } : undefined}
  >
    <p className="text-[14px] font-bold mb-4 conthrax-font text-[#1E3A8A]">{title}</p>
    {children}
  </div>
);

const InteractiveProposalSignature = () => {
  const {
    selectedVersion,
    isPrimaryAdvisor,
    signatureDetails,
    fetchApprovalDetails,
    fetchProposalSummary,
    signatureLoading: loading,
    proposalStatus,
    isPrimaryContactUser,
    isAgencyManager,
    isSubAgencyManager,
  } = useIPContext();
  const { t } = useTranslation();
  const planInfo = getSessionStore("planInfo");
  const currentProposalType = getSessionStore("currentProposalType");
  const { branding } = getSessionStore("lookUpResponse");
  const { secondaryColor } = getThemeColors(branding);
  const proposalId =
    selectedVersion?.id ??
    getSessionStore("proposalID") ??
    getSessionStore("interactiveProposalID");
  const toast: MutableRefObject<any> = useRef(null);
  const { openComments, getComments, setIsCurrPageLoading } = useComments();
  const userAccess = getObjectFromLocalStore("useraccess");
  const { isAgencyManagerActive } = useSelector(getIsAgencyManagerActiveData);
  const changesRequested = proposalStatus === PROPOSAL_STATUSES.FEEDBACK;

  const handleResolveContactComments = async () => {
    try {
      await makeRequest("resolveAllContactComments").put(proposalId, undefined, true);
      toast?.current?.show({
        severity: TOASTTYPE.SUCCESS,
        summary: t("agentBranding.label.success"),
        detail: "Comments resolved successfully.",
        life: 3000,
      });
      getComments();
    } catch (error: any) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error?.message || "Failed to resolve comments.",
        life: 3000,
      });
    }
  };

  const isContactUser = userAccess?.userType === USERTYPE.CONTACT;
  const isAdvisorUser = userAccess?.userType === USERTYPE.AGENT;
  const isPrimaryAdvisorOrAgencyManager = () =>
    isPrimaryAdvisor || isAgencyManager || isSubAgencyManager;
  const openCommentsCount = React.useMemo(
    () => getOpenOrUnresolvedComments(openComments, userAccess?.userType),
    [openComments, userAccess?.userType],
  );
  CommonHookForAPI(() => {
    setIsCurrPageLoading(true);
    fetchApprovalDetails()
      .then(() => {
        setIsCurrPageLoading(false, true);
      })
      .catch(() => {
        setIsCurrPageLoading(false, true);
      });
  });
  const isPlanOrInactive = [
    PROPOSAL_STATUSES.PLAN_CREATED,
    PROPOSAL_STATUSES.REVOKED,
    PROPOSAL_STATUSES.IN_ACTIVE,
  ].includes(proposalStatus as any);

  const contactInfo = signatureDetails?.contact?.profile;
  const advisorInfo = signatureDetails?.contact?.contactAdvisors?.find(
    (advisor: any) => advisor?.agentType === "primary",
  )?.agent?.profile;

  const clientName = contactInfo
    ? capitalizeNames(`${contactInfo?.firstName ?? ""} ${contactInfo?.lastName ?? ""}`)
    : "";
  const advisorName = advisorInfo
    ? capitalizeNames(`${advisorInfo?.firstName ?? ""} ${advisorInfo?.lastName ?? ""}`)
    : "";
  // A signer is "Approved" once they confirm (or once the signed proposal is uploaded), and the
  // proposal is not in a rejected state — mirrors the Approve/Approved button label logic below.
  const contactApproved =
    !signatureDetails?.signedProposalRejected &&
    Boolean(signatureDetails?.contactApprovalStatus || signatureDetails?.signedProposalFileKey);
  const advisorApproved =
    !signatureDetails?.signedProposalRejected &&
    Boolean(signatureDetails?.advisorApprovalStatus || signatureDetails?.signedProposalFileKey);

  const handleSeenComment = async () => {
    if (openComments?.length > 0) {
      makeRequest("proposalRequestCommentChanges")
        .post(
          [],
          currentProposalType === CurrentProposalType.PLANS
            ? selectedVersion?.id ??
                planInfo?.planProposalId ??
                planInfo?.planId ??
                getSessionStore("interactiveProposalID") ??
                getSessionStore("proposalID")
            : proposalId,
          true,
        )
        .then(() => {
          toast?.current?.show({
            severity: TOASTTYPE.SUCCESS,
            summary: t("agentBranding.label.success"),
            detail: "Requested changes has been added to proposal",
            life: 3000,
          });

          getComments();
        })
        .catch(() => {
          toast?.current?.show({
            severity: TOASTTYPE.ERROR,
            summary: t("agentBranding.label.error"),
            detail: "Error in requesting changes",
            life: 3000,
          });
        })
        .finally(() => {
          fetchApprovalDetails();
          fetchProposalSummary();
        });
    }
  };
  const approvalAdress: any = {
    [USERTYPE.AGENT]: "interactiveProposalAdvisorProposalApproval",
    [USERTYPE.CONTACT]: "interactiveProposalContactProposalApproval",
    [USERTYPE.ADMIN]: "interactiveProposalAdminProposalApproval",
  };
  async function approveSignatureProposal() {
    try {
      const response = await makeRequest(approvalAdress[userAccess?.userType]).patch(
        undefined, // No body needed
        currentProposalType === CurrentProposalType.PLANS
          ? selectedVersion?.id ??
              planInfo?.planProposalId ??
              planInfo?.planId ??
              getSessionStore("interactiveProposalID") ??
              getSessionStore("proposalID")
          : proposalId,
        true, // Indicates that `proposalId` is a request parameter
      );

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: response?.data?.message,
      });
    } catch (error: any) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error?.message,
      });
    } finally {
      setTimeout(() => {
        fetchApprovalDetails();
        fetchProposalSummary();
      }, 3000);
    }
  }
  const isUploadBtnDisabled = () => {
    if (userAccess?.userType === USERTYPE.AGENT) {
      return signatureDetails?.advisorApprovalStatus;
    }
    if (userAccess?.userType === USERTYPE.CONTACT) {
      return signatureDetails?.contactApprovalStatus;
    }
    return false;
  };

  const isUploadButtonDisabled = () => {
    if (changesRequested || isUploadBtnDisabled()) {
      return true;
    }
    if (isPlanOrInactive) {
      return true;
    }
    if (
      (isContactUser || isAdvisorUser) &&
      (isPrimaryContactUser || isPrimaryAdvisor || isAgencyManager || isSubAgencyManager)
    ) {
      if (
        (signatureDetails?.status === "received" || signatureDetails?.status === "signed") &&
        !signatureDetails?.signedProposalFileKey
      ) {
        return false;
      }
    }
    if (signatureDetails?.signedProposalFileKey && signatureDetails?.status === "uploaded") {
      return true;
    }
    if (isContactUser) {
      if (!isPrimaryContactUser) {
        return true;
      }
      if (signatureDetails?.signedProposalRejected) {
        return true;
      }

      if (
        !isPrimaryContactUser ||
        (signatureDetails?.signedProposalFileKey && signatureDetails?.status === "uploaded")
      ) {
        return true;
      }
    }
    if (isAdvisorUser) {
      if (!isPrimaryAdvisor) {
        return true;
      }
      if (signatureDetails?.signedProposalRejected) {
        return true;
      }
      if (!isPrimaryAdvisor && !isAgencyManagerActive) {
        return true;
      }

      if (!isPrimaryAdvisor && !(isAgencyManager || isSubAgencyManager)) {
        return true;
      }
    }
    return false;
  };
  const requestChangesIcon = () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="h-4"
    >
      <path
        d="M7.83366 1.1665L10.8337 3.99984M10.8337 3.99984L7.83366 6.83317M10.8337 3.99984H3.83366C2.3609 3.99984 1.16699 5.19374 1.16699 6.6665V10.8332"
        stroke={secondaryColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  // Primary Contact / Insured(s) signer cell (same person in the Interactive Proposal flow):
  // pending → outlined pill + Approve (or Request/Resolve Comments); approved → green chip + date.
  const renderContactSigner = () => {
    if (isContactUser && openCommentsCount?.length > 0) {
      return (
        <BrandingIconButton
          label="Resolve Comments"
          className=" rounded-3xl flex items-center !px-2 !py-1 cursor-pointer"
          color="#F3164E"
          onClick={handleResolveContactComments}
        />
      );
    }
    if (isContactUser && (openComments?.length > 0 || changesRequested)) {
      return <PendingPill name={clientName} />;
    }
    if ((openComments?.length > 0 || changesRequested) && !isContactUser) {
      return (
        <BrandingIconButton
          label={signatureDetails?.status === "feedback" ? "Changes Requested" : "Request Changes"}
          disabled={isPlanOrInactive ? true : signatureDetails?.status === "feedback"}
          className=" rounded-3xl flex items-center !px-2 !py-1 cursor-pointer"
          style={{ border: `1px solid ${secondaryColor}` }}
          onClick={handleSeenComment}
          icon={requestChangesIcon}
        />
      );
    }
    if (contactApproved) {
      return (
        <>
          <SignedPill name={clientName} />
          {signatureDetails?.contactApprovalDate && (
            <ApprovedTimestamp value={formatApprovedAt(signatureDetails.contactApprovalDate)} />
          )}
        </>
      );
    }
    return (
      <div className="flex flex-col items-center gap-2">
        <PendingPill name={clientName} />
        <BrandingIconButton
          label="Approve"
          disabled={
            signatureDetails?.signedProposalRejected && signatureDetails?.signedProposalFileKey
              ? true
              : isPlanOrInactive
              ? true
              : !isContactUser
              ? true
              : !isPrimaryContactUser
          }
          icon={() => <FaRegCircleCheck className="h-7" style={{ color: secondaryColor }} />}
          onClick={() => approveSignatureProposal()}
          className="!px-2 !py-1"
        />
      </div>
    );
  };

  // Advisor signer cell.
  const renderAdvisorSigner = () => {
    if (openComments?.length > 0 || changesRequested) {
      return (
        <BrandingIconButton
          label={signatureDetails?.status === "feedback" ? "Changes Requested" : "Request Changes"}
          disabled={
            isPlanOrInactive
              ? true
              : !isPrimaryAdvisorOrAgencyManager()
              ? true
              : signatureDetails?.status === "feedback"
          }
          className=" rounded-3xl flex items-center !px-2 !py-1 cursor-pointer"
          style={{ border: `1px solid ${secondaryColor}` }}
          onClick={handleSeenComment}
          icon={requestChangesIcon}
        />
      );
    }
    if (advisorApproved) {
      return (
        <>
          <SignedPill name={advisorName} />
          {signatureDetails?.advisorApprovalDate && (
            <ApprovedTimestamp value={formatApprovedAt(signatureDetails.advisorApprovalDate)} />
          )}
        </>
      );
    }
    return (
      <div className="flex flex-col items-center gap-2">
        <PendingPill name={advisorName} />
        <BrandingIconButton
          label="Approve"
          disabled={
            signatureDetails?.signedProposalRejected && signatureDetails?.signedProposalFileKey
              ? true
              : isPlanOrInactive
              ? true
              : !isPrimaryAdvisorOrAgencyManager()
          }
          icon={() => <FaRegCircleCheck className="h-7" style={{ color: secondaryColor }} />}
          onClick={() => approveSignatureProposal()}
          className="!px-2 !py-1"
        />
      </div>
    );
  };
  return (
    <CommentLayout
      commentLayoutId="interactive-proposal-signature"
      className="border border-1 border-gray-300 rounded-2xl w-full  shadow-lg dark:!border-[#3A3A50] dark:bg-[#2A2A3D] h-[calc(100vh-111px)]"
    >
      <Toast ref={toast} position="top-center" />
      {loading && <ScreenLoader />}
      <div>
        <div className="p-3">
          <h2 className="text-xl font-semibold mb-3 conthrax-font">Signature</h2>
          <UploadSection
            fetchApprovalDetails={fetchApprovalDetails}
            signatureDetails={signatureDetails}
            isUploadButtonDisabled={isUploadButtonDisabled()}
            isOpenComments={openComments?.length > 0}
            isAdmin={userAccess?.userType === USERTYPE.ADMIN}
            // eslint-disable-next-line react/jsx-no-bind
            onApprove={approveSignatureProposal}
            isProposalInactive={isPlanOrInactive}
          />
        </div>
        <Divider className="custom-divider dark:!border-[#3A3A50]" />
        <div className="p-3">
          <div className="grid grid-cols-2 py-3">
            <SignerColumn title="Primary Contact" hasBorderRight>
              {renderContactSigner()}
            </SignerColumn>
            <SignerColumn title="Advisor">{renderAdvisorSigner()}</SignerColumn>
          </div>
        </div>
      </div>
    </CommentLayout>
  );
};

export default InteractiveProposalSignature;
